/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'

export type AuthInfo = {
  authenticated?: boolean,
  expiresAt?: number,
  permissions?: string[],
  roleId?: number,
  username?: string,
}

export function getPermissions (context?: AuthInfo): string[] {
  return context?.permissions || []
}

export function isSignedIn (context?: AuthInfo): boolean {
  return context?.username != null
}

enum ActionTypes {
  REFRESH = 'REFRESH',
  REFRESH_ERROR = 'REFRESH_ERROR',
  REFRESH_SUCCESS = 'REFRESH_SUCCESS',
  SIGN_IN = 'SIGN_IN',
  SIGN_IN_ERROR = 'SIGN_IN_ERROR',
  SIGN_IN_SUCCESS = 'SIGN_IN_SUCCESS',
  SIGN_OUT = 'SIGN_OUT',
  SIGN_OUT_ERROR = 'SIGN_OUT_ERROR',
  SIGN_OUT_SUCCESS = 'SIGN_OUT_SUCCESS',
}

type State = {
  error?: Error
  ready: boolean
  refreshing: boolean
  signedIn: boolean
  signingIn: boolean
  signingOut: boolean
}

type Action = { type: ActionTypes.REFRESH }
  | { type: ActionTypes.REFRESH_ERROR, error: Error }
  | { type: ActionTypes.REFRESH_SUCCESS, data: { signedIn: boolean } }
  | { type: ActionTypes.SIGN_IN }
  | { type: ActionTypes.SIGN_IN_ERROR, error: Error }
  | { type: ActionTypes.SIGN_IN_SUCCESS }
  | { type: ActionTypes.SIGN_OUT }
  | { type: ActionTypes.SIGN_OUT_ERROR, error: Error }
  | { type: ActionTypes.SIGN_OUT_SUCCESS }

function reducer (state: State, action: Action) {
  const { type } = action
  let nextState = { ...state }

  if (type === ActionTypes.REFRESH) {
    nextState = {
      ...state,
      refreshing: true
    }
  } else if (type === ActionTypes.REFRESH_ERROR) {
    nextState = {
      ...state,
      error: action.error,
      refreshing: false
    }
  } else if (type === ActionTypes.REFRESH_SUCCESS) {
    nextState = {
      ...state,
      refreshing: false,
      ready: true,
      signedIn: action.data.signedIn
    }
  } else if (type === ActionTypes.SIGN_IN) {
    nextState = {
      ...state,
      error: undefined,
      signingIn: true,
      signingOut: false
    }
  } else if (type === ActionTypes.SIGN_IN_ERROR) {
    nextState = {
      ...state,
      error: action.error,
      signingIn: false
    }
  } else if (type === ActionTypes.SIGN_IN_SUCCESS) {
    nextState = {
      ...state,
      signingIn: false,
      signedIn: true
    }
  } else if (type === ActionTypes.SIGN_OUT) {
    nextState = {
      ...state,
      error: undefined,
      signingIn: false,
      signingOut: true
    }
  } else if (type === ActionTypes.SIGN_OUT_ERROR) {
    nextState = {
      ...state,
      error: action.error,
      signingOut: false
    }
  } else if (type === ActionTypes.SIGN_OUT_SUCCESS) {
    nextState = {
      ...state,
      signingOut: false,
      signedIn: false
    }
  }
  return nextState
}

export type UseAuthOptions<C> = {
  adminPermission?: string
  getPermissions (context: C): string[]
  isSignedIn (context: C): boolean
  refresh (): Promise<C>
  signInFunc (username: string, password: string): Promise<C>
  signInUrl?: string
  signOutFunc (): Promise<any>
}

export type UseAuthHook<C> = State & {
  context?: C
  hasPermission (...perms: string[]): boolean
  signIn (username: string, password: string): Promise<C>
  signInUrl: string
  signOut (): Promise<any>
}

function useAuth<C> (options: UseAuthOptions<C>): UseAuthHook<C> {
  const {
    adminPermission = 'admin',
    getPermissions,
    isSignedIn,
    refresh,
    signInFunc,
    signInUrl,
    signOutFunc
  } = options

  const [context, setContext] = useState<C>()

  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    error: undefined,
    ready: false,
    refreshing: false,
    signedIn: isSignedIn(context),
    signingIn: false,
    signingOut: false
  }))

  /**
   * Checks if the current auth context contains a permission in the list.
   * @param permission
   */
  const hasPermission = useCallback((...permissions: string[]): boolean => {
    const userPermissions = getPermissions(context)

    if (userPermissions) {
      // Always return true for admin.
      if (adminPermission && userPermissions.includes(adminPermission)) {
        return true
      }
      // Check permissions.
      for (let i = 0; i < permissions.length; i += 1) {
        if (userPermissions.includes(permissions[i])) {
          return true
        }
      }
    }
    return false
  }, [adminPermission, context, getPermissions])

  /**
   * Signs-in user.
   */
  const signIn = useCallback((username: string, password: string): Promise<C> => {
    dispatch({ type: ActionTypes.SIGN_IN })
    return signInFunc(username, password)
      .then((result) => {
        setContext(result)
        dispatch({ type: ActionTypes.SIGN_IN_SUCCESS })
        return result
      })
      .catch((error) => {
        dispatch({ type: ActionTypes.SIGN_IN_ERROR, error })
        // Propagate error.
        throw error
      })
  }, [signInFunc])

  /**
   * Signs-out user.
   */
  const signOut = useCallback((): Promise<void> => {
    dispatch({ type: ActionTypes.SIGN_OUT })
    return signOutFunc()
      .then((result) => {
        setContext(undefined)
        dispatch({ type: ActionTypes.SIGN_OUT_SUCCESS })
        return result
      })
      .catch((error) => {
        dispatch({ type: ActionTypes.SIGN_OUT_ERROR, error })
        // Propagate error.
        throw error
      })
  }, [signOutFunc])

  useEffect(() => {
    dispatch({ type: ActionTypes.REFRESH })
    refresh()
      .then((result) => {
        setContext(result)
        dispatch({
          type: ActionTypes.REFRESH_SUCCESS,
          data: { signedIn: isSignedIn(result) }
        })
      })
      .catch((error) => {
        dispatch({ type: ActionTypes.REFRESH_ERROR, error })
      })
  }, [isSignedIn, refresh])

  return useMemo(() => ({
    context,
    ...state,
    hasPermission,
    signIn,
    signInUrl,
    signOut
  }), [context, hasPermission, signIn, signInUrl, signOut, state])
}

export default useAuth
